import qz from "qz-tray";
import { api } from "../api/axios";
import { API_BASE_URL } from "./constants";
import { PrinterResolution, listPrinters, pickPrinter, resolvePrinter, saveSavedPrinter } from "./printerPicker";
import { toast } from "react-toastify";
import { t } from "i18next";

/**
 * QZ Tray trusts requests signed by the key behind its certificate. The
 * private key lives only on the shopend server: the certificate is fetched
 * from the backend and every signature is requested from POST /print/sign,
 * which needs a logged-in user allowed to print receipts.
 */
export const QZ_CERTIFICATE_URL = "print/certificate";
export const QZ_SIGN_URL = "print/sign";

type SignClient = { post: (url: string, body: unknown, config?: any) => Promise<any> };

export async function fetchPemText(url: string): Promise<string> {
	const res = await fetch(url, { cache: "no-store" });
	if (!res.ok) {
		throw new Error(`Failed to fetch ${url}: HTTP ${res.status}`);
	}
	const text = await res.text();
	if (!text.includes("-----BEGIN")) {
		throw new Error(`Expected a PEM file from ${url}, got a non-PEM response`);
	}
	return text;
}

/** Certificate URL on the API (public endpoint). */
export function qzCertificateUrl(baseUrl: string = API_BASE_URL): string {
	return `${baseUrl.replace(/\/?$/, "/")}${QZ_CERTIFICATE_URL}`;
}

/** Ask the backend to sign a QZ request; resolves the base64 SHA512withRSA signature. */
export async function requestQzSignature(toSign: string, client: SignClient = api): Promise<string> {
	const res = await client.post(QZ_SIGN_URL, { request: toSign }, { responseType: "text", skipErrorToast: true });
	const signature = typeof res?.data === "string" ? res.data.trim() : "";
	if (res?.status !== 200 || !signature) {
		throw new Error(`QZ signing failed: HTTP ${res?.status ?? "no response"}`);
	}
	return signature;
}

let qzSecurityConfigured = false;

/** Certificate + signing hooks. Safe to call more than once. */
function configureQzSecurity(): void {
	if (qzSecurityConfigured) return;
	qzSecurityConfigured = true;

	qz.api.setPromiseType((resolver: (resolve: (value: unknown) => void, reject: (reason?: any) => void) => void): Promise<unknown> => {
		return new Promise(resolver);
	});

	qz.security.setSignatureAlgorithm("SHA512");

	qz.security.setCertificatePromise((resolve: (cert: string) => void, reject: (reason?: any) => void) => {
		fetchPemText(qzCertificateUrl()).then(resolve).catch(reject);
	});

	qz.security.setSignaturePromise((toSign: string): (resolve: (sig: string) => void, reject: (reason?: any) => void) => void => {
		return (resolve, reject) => {
			requestQzSignature(toSign)
				.then(resolve)
				.catch((err) => {
					console.error("Signing error:", err);
					reject(err);
				});
		};
	});
}

/** Configure QZ certificate/signing and connect. Returns a disconnect cleanup. */
export function setupQzTray(): () => void {
	configureQzSecurity();

	qz.websocket.connect()
		.then(() => {
			console.info("Connected to QZ Tray");
			// Keep the stored system default fresh for the status bar.
			return listPrinters(qz.printers);
		})
		.catch((err: unknown) => console.error("Connection to QZ Tray failed:", err));

	return () => {
		qz.websocket.disconnect()
			.then(() => console.info("Disconnected from QZ Tray"))
			.catch((err: unknown) => console.error("Disconnection failed:", err));
	};
}

/**
 * Connect on demand, for screens that print only now and then (payment and
 * wallet receipts) and so do not hold a QZ connection open like the POS.
 */
export async function ensureQzConnected(): Promise<boolean> {
	configureQzSecurity();
	if (qz.websocket.isActive()) return true;
	try {
		await qz.websocket.connect();
		return true;
	} catch (err) {
		// A screen's setupQzTray() may still be connecting: wait for it.
		if (String((err as any)?.message ?? err).includes("already exists")) {
			for (let i = 0; i < 30 && !qz.websocket.isActive(); i++) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}
			return qz.websocket.isActive();
		}
		console.error("Connection to QZ Tray failed:", err);
		return false;
	}
}

/**
 * Fetch a base64 PDF and send it to the chosen QZ printer. Shows the PRINT
 * loading dialog while downloading. `fetchPdf` returns the API response whose
 * `data.data` holds the PDF.
 */
export async function printBase64Pdf(
	fetchPdf: () => Promise<any>,
	setLoadingMessage: (message: string | null) => void,
): Promise<boolean> {
	if (!(await ensureQzConnected())) {
		toast.error(t("common:toast.error.noPrinter"), { autoClose: 4000 });
		return false;
	}
	let resolution: PrinterResolution;
	try {
		// Resolved before the loading overlay so the picker dialog stays usable.
		resolution = await resolvePrinter(qz.printers);
	} catch (err) {
		console.error("Printer lookup failed:", err);
		resolution = { printer: null, reason: "none" };
	}
	const printer = resolution.printer;
	if (!printer) {
		// A cancelled picker is the user's choice: no error toast.
		if (resolution.reason === "none") toast.error(t("common:toast.error.noPrinter"), { autoClose: 4000 });
		return false;
	}
	setLoadingMessage("PRINT");
	try {
		const config = qz.configs.create(printer);
		const res = await fetchPdf();
		if (res?.status === 200 && res.data?.data) {
			await qz.print(config, [
				{
					type: "pdf",
					format: "base64",
					data: res.data.data,
				},
			]);
			toast.success(t("common:toast.success.printSent", { printer }), { autoClose: 2500 });
			return true;
		}
		console.error("Failed to fetch receipt:", res);
		// Backend refusals are already toasted by the API client.
		if (!res || res.status >= 500) toast.error(t("common:toast.error.printFailed"), { autoClose: 4000 });
		return false;
	} catch (err) {
		console.error("Print failed:", err);
		toast.error(t("common:toast.error.printFailed"), { autoClose: 4000 });
		return false;
	} finally {
		setLoadingMessage(null);
	}
}

/**
 * Status bar entry: list this computer's printers and let the user choose the
 * app printer (or go back to the system default).
 */
export async function openPrinterSettings(): Promise<void> {
	const load = async () => ((await ensureQzConnected()) ? listPrinters(qz.printers) : null);
	const listed = await load().catch(() => null);
	const choice = await pickPrinter(listed?.printers ?? [], {
		mode: "settings",
		systemDefault: listed?.systemDefault ?? null,
		unavailable: !listed,
		reload: () => load().catch(() => null),
	});
	// Settings mode: remember=true saves the printer; printer=null with remember=true clears the app choice.
	if (choice.remember) saveSavedPrinter(choice.printer);
}

/** Fetch the sale receipt PDF and send it to the QZ printer. */
export async function printReceiptPdf(
	api: { get: (url: string) => Promise<any> },
	invoiceId: string,
	setLoadingMessage: (message: string | null) => void,
): Promise<boolean> {
	if (!invoiceId) return false;
	return printBase64Pdf(() => api.get(`/receipt/${invoiceId}`), setLoadingMessage);
}
